import { useMemo, useState } from 'react';

import {
  usePartnerProducts,
  deriveCategories,
  type PartnerProduct,
} from '@/hooks/usePartnerProducts';
import { useExperiences, type Experience } from '@/hooks/useExperiences';
import { Card, Skeleton } from '@/components/ui';
import { StoreHero } from '@/components/store/StoreHero';
import { StoreProductCard } from '@/components/store/StoreProductCard';
import { StoreExperienceCard } from '@/components/store/StoreExperienceCard';
import { useCart } from '@/store/cart';
import { toast } from '@/components/ui/Toast';

/**
 * Store — ROUND7 P1 / ROUND8. Listing público de productos partners con
 * filtro por categoría (chips). Add-to-cart inline.
 *
 * Datos reales desde `public.partner_products` vía `usePartnerProducts()`.
 *
 * REDISEÑO VISUAL (solo estético, SPEC-015): mismo lenguaje premium del Home /
 * Eventos — hero oscuro inmersivo (`StoreHero`) con imagen gastronómica real
 * fundida, y cards con media real (`StoreProductCard` / `StoreExperienceCard`,
 * vía `HomeMedia`). Cero cambios de datos, hooks, filtros, add-to-cart, rutas
 * ni el tab de Experiencias.
 */
/** Categoría sintética para experiencias VIP — no vive en partner_products. */
const EXPERIENCES_FILTER_SLUG = 'experiencias';

export function StorePage(): JSX.Element {
  const [filter, setFilter] = useState<string>('all');
  const { data: products, isLoading, isError } = usePartnerProducts();
  // ROUND19 #7: la pestaña "Experiencias" del store usa EL MISMO hook
  // (`useExperiences` → `v_active_experiences`) que /experiences. Se carga
  // siempre — es barato (5min staleTime) y mantiene la lista perfectamente
  // alineada con la página dedicada.
  const {
    data: experiences,
    isLoading: experiencesLoading,
    isError: experiencesError,
  } = useExperiences();
  const addProductLine = useCart((s) => s.addProductLine);

  const partnerCategories = useMemo(() => deriveCategories(products ?? []), [products]);

  // Inyectamos siempre la chip "Experiencias" (incluso si partner_products no
  // contiene esa categoría) cuando hay experiencias VIP disponibles, así el
  // hook compartido es la fuente canónica del tab.
  const hasExperiences = (experiences ?? []).length > 0;
  const categories = useMemo(() => {
    const filtered = partnerCategories.filter((c) => c.slug !== EXPERIENCES_FILTER_SLUG);
    if (!hasExperiences) return filtered;
    return [...filtered, { slug: EXPERIENCES_FILTER_SLUG, label: 'Experiencias' }];
  }, [partnerCategories, hasExperiences]);

  const showExperiencesView = filter === EXPERIENCES_FILTER_SLUG;
  const visible =
    filter === 'all'
      ? (products ?? []).filter((p) => p.category !== EXPERIENCES_FILTER_SLUG)
      : showExperiencesView
        ? []
        : (products ?? []).filter((p) => p.category === filter);

  // Fondo del hero: la imagen de producto REAL más representativa del catálogo
  // (gastronomía / bebidas / vinos). Local, sin inventar assets; sin imagen →
  // null (hero a gradiente).
  const heroImage = pickStoreBackdrop(products ?? []);

  function handleAdd(p: PartnerProduct): void {
    addProductLine({
      productId: p.id,
      partnerId: p.partnerId,
      partnerName: p.partnerName,
      title: p.title,
      unitPriceCents: p.priceCents,
      quantity: 1,
    });
    toast.success({ title: 'Agregado al carrito', description: `${p.title} · ${p.partnerName}` });
  }

  /** ROUND19 #4/#7: agregar experiencia VIP al carrito vía productLine. */
  function handleAddExperience(exp: Experience): void {
    addProductLine({
      productId: `experience:${exp.id}`,
      partnerId: 'first-class-experiences',
      partnerName: 'Experiencias First Class',
      title: exp.title,
      unitPriceCents: exp.priceCents,
      quantity: 1,
    });
    toast.success({ title: 'Agregada al carrito', description: exp.title });
  }

  return (
    <div>
      <StoreHero
        title="Tienda partners"
        subtitle="Experiencias, productos y servicios curados por los partners First Class."
        heroImage={heroImage}
      />

      <section className="container-fc py-12 md:py-16">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <Card variant="outlined" padded>
            <p className="text-sm text-fc-muted" role="alert">
              No pudimos cargar la tienda en este momento. Probá recargar la página en unos minutos.
            </p>
          </Card>
        ) : !products || products.length === 0 ? (
          <Card variant="outlined" padded>
            <p className="text-sm text-fc-muted">
              No hay productos disponibles por ahora. Volvé más tarde.
            </p>
          </Card>
        ) : (
          <>
            <nav aria-label="Categorías" className="mb-6 flex flex-wrap gap-2">
              <CategoryChip active={filter === 'all'} onClick={() => setFilter('all')}>
                Todas
              </CategoryChip>
              {categories.map((c) => (
                <CategoryChip
                  key={c.slug}
                  active={filter === c.slug}
                  onClick={() => setFilter(c.slug)}
                >
                  {c.label}
                </CategoryChip>
              ))}
            </nav>

            {showExperiencesView ? (
              // ROUND19 #7: render dedicado para experiencias VIP usando el
              // mismo hook que /experiences.
              experiencesLoading ? (
                <div
                  className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
                  aria-busy="true"
                >
                  {[0, 1, 2].map((i) => (
                    <ProductCardSkeleton key={i} />
                  ))}
                </div>
              ) : experiencesError ? (
                <Card variant="outlined" padded>
                  <p className="text-sm text-fc-muted" role="alert">
                    No pudimos cargar las experiencias en este momento.
                  </p>
                </Card>
              ) : (experiences ?? []).length === 0 ? (
                <Card variant="outlined" padded>
                  <p className="text-sm text-fc-muted">
                    No hay experiencias disponibles por ahora. Volvé más tarde.
                  </p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {(experiences ?? []).map((exp) => (
                    <StoreExperienceCard
                      key={exp.id}
                      experience={exp}
                      onAdd={handleAddExperience}
                    />
                  ))}
                </div>
              )
            ) : visible.length === 0 ? (
              <Card variant="outlined" padded>
                <p className="text-sm text-fc-muted">
                  No hay productos en esta categoría por ahora.
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {visible.map((p) => (
                  <StoreProductCard key={p.id} product={p} onAdd={handleAdd} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * Imagen de fondo del hero: la portada de producto que más se repite (imagen
 * "de la casa" del catálogo → gastronomía / bebidas). Sin portadas → null.
 */
function pickStoreBackdrop(products: ReadonlyArray<PartnerProduct>): string | null {
  const counts = new Map<string, number>();
  for (const p of products) {
    if (p.imageUrl) counts.set(p.imageUrl, (counts.get(p.imageUrl) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [url, n] of counts) {
    if (n > bestCount) {
      best = url;
      bestCount = n;
    }
  }
  return best;
}

/** Skeleton que replica la silueta de las cards de tienda durante la carga. */
function ProductCardSkeleton(): JSX.Element {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm">
      <Skeleton shape="aspect-[4/3] w-full rounded-none" srLabel="Cargando tienda…" />
      <div className="flex flex-1 flex-col gap-2 p-4 md:p-5">
        <Skeleton shape="h-5 w-4/5" srLabel="" />
        <Skeleton shape="h-4 w-1/2" srLabel="" />
        <div className="mt-4 flex items-center justify-between border-t border-fc-line/70 pt-4">
          <Skeleton shape="h-5 w-20" srLabel="" />
          <Skeleton shape="h-8 w-28" srLabel="" />
        </div>
      </div>
    </div>
  );
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        active
          ? 'bg-primary text-fc-bg shadow-sm'
          : 'border border-fc-line bg-neutral-50 text-neutral-700 hover:bg-neutral-100',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
